import * as prismic from '@prismicio/client'

import {
	AudurLegal,
	AudurLegalConnectionConnection,
	AudurProductConnectionConnection,
	AudurMarketInterestRatesConnectionConnection,
	FaqCategoryConnectionConnection,
	FaqCategory,
	Query,
	Maybe,
	AudurContentPage,
	AudurContentPageConnectionConnection,
	AudurFooterConnectionConnection,
	CreateAccountPageConnectionConnection,
	AudurHeaderConnectionConnection,
	AudurMortgagePageConnectionConnection,
	MortgageSubPageConnectionConnection,
	MortgageSubPage,
	Faq_pageConnectionConnection,
} from './prismicTypes'
import { prismicConstants } from './prismicUtils'

const { refApiUrl, apiLocale, apiDefaultLocale, gqlApiUrl } = prismicConstants

enum StatusCode {
	OK = 200,
}

type PreviewData = {
	ref: string
}

interface Variables {
	uId?: string
	lang?: string
}

type APIOptions = {
	previewData?: PreviewData
	variables?: Variables
}

export class AudurPrismicClient {
	private prismicClient: prismic.Client

	constructor(apiToken: string) {
		this.prismicClient = prismic.createClient(refApiUrl, {
			accessToken: apiToken,
		})
	}

	private async fetchAPI(query: string, { previewData, variables }: APIOptions = {}): Promise<Query> {
		// see: https://github.com/birkir/gatsby-source-prismic-graphql/commit/57e2940342f724ef21bc951b987a409fbc2d9d82
		const cleanQuery = query
			.replace(/#.*\n/g, '')
			.replace(/\s+/g, ' ')
			.replace(/\s?\{\s?/g, '{')
			.replace(/\s?\}\s?/g, '}')
			.replace(/\s?:\s?/g, ':')
			.replace(/\s?\(\s?/g, '(')
			.replace(/\s?\)\s?/g, ')')

		const { accessToken } = this.prismicClient
		const requestHeaders: HeadersInit = new Headers()
		const masterRef = await this.prismicClient.getMasterRef()
		requestHeaders.set('Content-Type', 'application/json')
		requestHeaders.set('Prismic-Ref', previewData?.ref || masterRef.ref)
		requestHeaders.set('Accept-Language', apiLocale ?? apiDefaultLocale)
		requestHeaders.set('Authorization', `Token ${accessToken}`)

		const res = await fetch(
			`${gqlApiUrl}?query=${encodeURIComponent(cleanQuery)}&variables=${encodeURIComponent(JSON.stringify(variables))}`,
			{
				headers: requestHeaders,
			}
		)

		if (res.status !== StatusCode.OK) {
			throw new Error('Prismic request failed with a non-200 response')
		}

		const json = await res.json()
		if (json.errors) {
			throw new Error('Error in Prismic JSON response')
		}
		return json.data
	}

	public getPrismicClient(): prismic.Client {
		return this.prismicClient
	}

	public async getMasterRef(): Promise<string> {
		const masterRef = await this.prismicClient.getMasterRef()
		return masterRef.ref
	}

	/**
	 *
	 * @param APIOptions: { apiToken, previewData, variables }
	 * @returns Promise<Maybe<AudurLegal>>
	 */
	public async getAudurLegalByUid({ previewData, variables }: APIOptions = {}): Promise<Maybe<AudurLegal>> {
		const data = await this.fetchAPI(
			`
      query getAudurLegalByUID($uId: String!, $lang: String!) {
        audurLegal(uid: $uId, lang: $lang) {
          title
          content
          showcustomcontent
          body {
            __typename
            ... on AudurLegalBodyMeta_tags {
              type
              primary {
                metaTitle
                metaDescription
                metaImage
              }
            }
          }
          _meta {
            id
            uid
            lang
            type
          }
        }
      }

    `,
			{
				previewData,
				variables,
			}
		)
		return data.audurLegal
	}

	/**
	 *
	 * @param APIOptions: { apiToken, previewData, variables }
	 * @returns Promise<Maybe<AudurLegalConnectionConnection>>
	 */
	public async getAllAudurLegals({ previewData, variables }: APIOptions = {}): Promise<
		Maybe<AudurLegalConnectionConnection>
	> {
		const data = await this.fetchAPI(
			`query getAllAudurLegals($lang: String!) {
        allAudurLegals(lang: $lang) {
          edges {
            node {
              _meta {
                uid
                lang
              }
            }
          }
        }
      }`,
			{
				previewData,
				variables,
			}
		)
		return data.allAudurLegals
	}

	/**
	 *
	 * @param APIOptions: { apiToken, previewData, variables }
	 * @returns Promise<Maybe<AudurProductConnectionConnection>>
	 */
	public async getAllProducts({ previewData, variables }: APIOptions = {}): Promise<
		Maybe<AudurProductConnectionConnection>
	> {
		const data = await this.fetchAPI(
			`query getAllAudurProducts($lang: String!) {
        allAudurProducts(lang: $lang) {
          edges {
            node {
              id
              type
              term
              interestRate
              indexation
              productName
              depositAvailable
              interestPayoutTerm
            }
          }
        }
      }`,
			{
				previewData,
				variables,
			}
		)
		return data.allAudurProducts
	}

	/**
	 *
	 * @param APIOptions: { apiToken, previewData, variables }
	 * @returns Promise<Maybe<AudurProductConnectionConnection>>
	 */
	public async getFooter({ previewData, variables }: APIOptions = {}): Promise<Maybe<AudurFooterConnectionConnection>> {
		const data = await this.fetchAPI(
			`query getFooter($lang: String!) {
        allAudurFooters(lang: $lang) {
          edges {
            node {
              image
              description
              phone_number {
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
              email {
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
              link {
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
              button {
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
              first_column {
                first_column_link {
                  ... on AudurLink {
                    linkUrl
                    linkTitle
                    linkAlt
                    linkImage
                    linkStyle
                  }
                }
              }
              second_column {
                second_column_link {
                  ... on AudurLink {
                    linkUrl
                    linkTitle
                    linkAlt
                    linkImage
                    linkStyle
                  }
                }
              }
            }
          }
        }
      }
      `,
			{
				previewData,
				variables,
			}
		)
		return data.allAudurFooters
	}

	/**
	 *
	 * @param APIOptions: { apiToken, previewData, variables }
	 * @returns Promise<Maybe<AudurMarketInterestRatesConnectionConnection>>
	 */
	public async getMarketInterestRates({ previewData, variables }: APIOptions = {}): Promise<
		Maybe<AudurMarketInterestRatesConnectionConnection>
	> {
		const data = await this.fetchAPI(
			`query getAllMarketInterestRates {
        allAudurMarketInterestRatess {
          edges {
            node {
              companyMarketInterestRates {
                lowDepositRate
                midDepositRate
                midToHighDepositRate
                highDepositRate
              }
              savingsMarketInterestRates {
                lowDepositRate
                midDepositRate
                midToHighDepositRate
                highDepositRate
              }
              termDepositMarketInterestRates {
                threeMonthTermRate
                sixMonthTermRate
                twelveMonthTermRate
              }
              futureMarketInterestRates {
                averageMarketFutureRate
              }
              inflationRateGoal
            }
          }
        }
      }
      `,
			{
				previewData,
				variables,
			}
		)
		return data.allAudurMarketInterestRatess
	}

	/**
	 *
	 * @param APIOptions: { apiToken, previewData, variables }
	 * @returns Promise<Maybe<AudurContentPage>>
	 */
	public async getAudurContentPageByUid({ previewData, variables }: APIOptions = {}): Promise<Maybe<AudurContentPage>> {
		const data = await this.fetchAPI(
			`query getContentPage($uId: String!, $lang: String!) {
        audurContentPage(uid: $uId, lang: $lang) {
          title
          pageLayout
          body {
            __typename
            ... on AudurContentPageBodyMeta_tags {
              type
              primary {
                metaTitle
                metaDescription
                metaImage
              }
            }
          }
          slices {
            __typename
            ... on AudurContentPageSlicesInteresttable {
              type
              fields {
                bank_account_type
                annual_interest_rate
                interest_payment_frequency
                indexation
                availability
                bank_account_type_slug
                bank_account_term_slug
                interest_on_an_annual_basis
              }
            }
            ... on AudurContentPageSlicesCalculator_faq {
              type
              primary {
                title
                description
                color
              }
              fields {
                faq_single_title
                faq_single_description
              }
            }
          }
          editorialContentGroup {
            contentPageEditorial {
              __typename
              ... on AudurEditorialContent {
                title
                description
                contentWidth
                contentAlignment
                contentType
                contentStyle
                image
                imageWidth
                imageHeight
                lottieImageName
                linkGroup {
                  editorialContentLink {
                    __typename
                    ... on AudurLink {
                      linkUrl
                      linkTitle
                      linkAlt
                      linkImage
                      linkStyle
                    }
                  }
                }
              }
            }
          }
          _meta {
            uid
            type
            lang
          }
        }
      }
    `,
			{
				previewData,
				variables,
			}
		)
		return data.audurContentPage
	}

	/**
	 *
	 * @param APIOptions: { apiToken, previewData, variables }
	 * @returns Promise<Maybe<AudurLegal>>
	 */
	public async getAllAudurContentPages({ previewData }: APIOptions = {}): Promise<
		Maybe<AudurContentPageConnectionConnection>
	> {
		const data = await this.fetchAPI(
			`
      query {
        allAudurContentPages {
          edges {
            node {
              title
              pageLayout
              editorialContentGroup {
                contentPageEditorial {
                  __typename
                  ... on AudurEditorialContent {
                    title
                    description
                    contentWidth
                    contentAlignment
                    contentType
                    contentStyle
                    image
                    imageWidth
                    imageHeight
                    lottieImageName
                    linkGroup {
                      editorialContentLink {
                        __typename
                        ... on AudurLink {
                          linkUrl
                          linkTitle
                          linkAlt
                          linkImage
                          linkStyle
                        }
                      }
                    }
                  }
                }
              }
              _meta {
                uid
                type
                lang
              }
            }
          }
        }
      }

        `,
			{
				previewData,
			}
		)
		return data.allAudurContentPages
	}

	/*
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<FaqCategoryConnectionConnection>>
	 */
	public async getAllFAQCategories({ previewData, variables }: APIOptions = {}): Promise<
		Maybe<FaqCategoryConnectionConnection>
	> {
		const data = await this.fetchAPI(
			`query getAllFAQCategories($lang: String!) {
        allFaqCategorys(lang: $lang) {
          edges {
            node {
              title
              image
              _meta {
                uid
                lang
              }
            }
          }
        }
      }`,
			{
				previewData,
				variables,
			}
		)
		return data.allFaqCategorys
	}

	/*
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<FaqCategory>>
	 */
	public async getFAQCategoryByUid({ previewData, variables }: APIOptions = {}): Promise<Maybe<FaqCategory>> {
		const data = await this.fetchAPI(
			`query getFAQCategoryByUID($uId: String!, $lang: String!) {
        faqCategory(uid: $uId, lang: $lang) {
          title
          description
          image
          faqItems {
            faqItemQuestion
            faqItemAnswer
          }
          body {
            __typename
            ... on FaqCategoryBodyMeta_tags {
              type
              primary {
                metaTitle
                metaDescription
                metaImage
              }
            }
          }
          _meta {
            id
            uid
            lang
            type
          }
        }
      }
      `,
			{
				previewData,
				variables,
			}
		)
		return data.faqCategory
	}

	/**
	 *
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<CreateAccountPageConnectionConnection>>
	 */
	public async getCreateAccountPage({ previewData, variables }: APIOptions = {}): Promise<
		Maybe<CreateAccountPageConnectionConnection>
	> {
		const data = await this.fetchAPI(
			` query getCreateAccountPage($lang: String!) {
          allCreateAccountPages(lang: $lang) {
            edges {
              node {
                title
                description
                button {
                  _linkType
                  ... on AudurLink {
                    linkUrl
                    linkTitle
                    linkAlt
                    linkImage
                    linkStyle
                  }
                }
                group {
                  item {
                    ... on CreateAccountItem {
                      image
                      title
                    }
                  }
                }
              }
            }
          }
        }

        `,
			{
				previewData,
				variables,
			}
		)
		return data.allCreateAccountPages
	}

	/**
	 *
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<AudurHeaderConnectionConnection>>
	 */
	public async getHeader({ previewData, variables }: APIOptions = {}): Promise<Maybe<AudurHeaderConnectionConnection>> {
		const data = await this.fetchAPI(
			` query getHeader($lang: String!) {
      allAudurHeaders(lang: $lang) {
        edges {
          node {
            image
            link_group {
              link {
                _linkType
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
            }
            button_group {
              button {
                _linkType
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
                ... on Audur_dropdown_link {
                  audur_dropdown_button_title
                  audur_dropdown_values {
                    audur_dropdown_value_text
                    audur_dropdown_value_link
                  }
                }
              }
            }
          }
        }
      }
    }
      `,
			{
				previewData,
				variables,
			}
		)
		return data.allAudurHeaders
	}

	/**
	 *
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<AudurMortgagePageConnectionConnection>>
	 */
	public async getAllMortgagePages({ previewData, variables = { lang: 'is-is' } }: APIOptions = {}): Promise<
		Maybe<AudurMortgagePageConnectionConnection>
	> {
		const data = await this.fetchAPI(
			` query getMortgagePages($lang: String!) {
      allMortgage_pages(lang: $lang) {
    edges {
      node {
        body {
          __typename
          ...on Mortgage_pageBodyCalculator {
            type
            primary {
              property_market_value_tooltip
              estimated_property_annual_increase_tooltip
              equalization_tooltip
              reduction_tooltip
              estimated_equity_x_years_tooltip
              loan_term_tooltip
              interest_tooltip
              interest_tooltip_fixed_option
              interest_tooltip_3year_option
              tex_free_allocation_tooltip_with_link
              payment_agreements_tooltip
              ahk_tooltip
              property_equity_tooltip
              estimated_monthly_payment_tooltip
              temporary_reduction_length_tooltip
              borrowing_cost_tooltip
            }
          }
          ... on Mortgage_pageBodyCalculator_faq {
            type
            primary {
              faq_title: title
              faq_description: description
              faq_color: color
              faq_cta_title: cta_title
              faq_cta_description: cta_description
              faq_cta_link: cta_link {
                _linkType
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
              faq_cta_link_text_2: cta_link_text_2
              faq_cta_title_2: cta_title_2
              faq_cta_description_2: cta_description_2
              faq_cta_link_2: cta_link_2 {
                _linkType
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
            }
            fields {
              faq_single_title
              faq_single_description
            }
          }
          ...on Mortgage_pageBodyHerowithcta {
            type
            primary {
              hero_title: title
              description
              apply_for_mortage_button
              loan_calculator_button
              image
              color
              disclamer
            }
          }
          ...on Mortgage_pageBodyTwo_paths_slice {
            type
            primary {
              two_paths_main_title: main_title
              main_description
              main_image
              first_option_title
              first_option_subtitle
              first_option_description
              second_option_title
              second_option_subtitle
              second_option_description
            }
          }
        }
      }
    }
  }
    }
      `,
			{
				previewData,
				variables,
			}
		)
		return data.allMortgage_pages
	}

	/**
	 *
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<AudurMortgagePageConnectionConnection>>
	 */
	public async getMortgageSubPage({ previewData, variables }: APIOptions = {}): Promise<Maybe<MortgageSubPage>> {
		const data = await this.fetchAPI(
			`query getMortgageSubPage($uId: String!, $lang: String!) {
    mortgage_sub_page(uid: $uId, lang: $lang)  {
        title
        body1 {
          ... on Mortgage_sub_pageBody1Meta_tags {
              type
              primary {
                metaTitle
                metaDescription
                metaImage
              }
          }
        }
        body {
          __typename
          ...on Mortgage_sub_pageBodyCalculator_faq {
            type
            primary {
              faq_title: title
              faq_description: description
              faq_color: color
              faq_cta_title: cta_title
              faq_cta_description: cta_description
              faq_cta_link: cta_link {
                _linkType
                ... on AudurLink {
                  linkUrl
                  linkTitle
                  linkAlt
                  linkImage
                  linkStyle
                }
              }
              faq_cta_link_text: cta_link_text
            }
            fields {
              faq_single_title
              faq_single_description
            }
          }
          ...on Mortgage_sub_pageBodyHerowithcta {
            type
            primary {
              title
              description
              apply_for_mortage_button
              loan_calculator_button
              image
              color
            }
          }
          ...on Mortgage_sub_pageBodyPricingtable {
            type
            primary {
              pricingTitle: title
            }
            fields {
              item_name
              item_price
            }
          }
          ...on Mortgage_sub_pageBodyInterestRateCb {
            type
            label
            primary {
              interestrate_cb_description
              interestrate_cb_chartTitle
              interestrate_cb_chartLine
            }
          }
        }
        editorialContentGroup {
            contentPageEditorial {
              __typename
              ... on AudurEditorialContent {
                title
                description
                contentWidth
                contentAlignment
                contentType
                contentStyle
                image
                imageWidth
                imageHeight
                lottieImageName
                linkGroup {
                  editorialContentLink {
                    __typename
                    ... on AudurLink {
                      linkUrl
                      linkTitle
                      linkAlt
                      linkImage
                      linkStyle
                    }
                  }
                }
              }
            }
          }
      }
    }
      `,
			{
				previewData,
				variables,
			}
		)
		return data.mortgage_sub_page
	}

	/*
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<FaqCategoryConnectionConnection>>
	 */
	public async getAllMortgageSubPages({ previewData, variables }: APIOptions = {}): Promise<
		Maybe<MortgageSubPageConnectionConnection>
	> {
		const data = await this.fetchAPI(
			`query getAllMortgageSubPages($lang: String!) {
        allMortgage_sub_pages(lang: $lang) {
          edges {
            node {
              title
              _meta {
                uid
                lang
              }
            }
          }
        }
      }`,
			{
				previewData,
				variables,
			}
		)
		return data.allMortgage_sub_pages
	}

	/*
	 * @param APIOptions: { previewData, variables }
	 * @returns Promise<Maybe<FaqCategoryConnectionConnection>>
	 */
	public async getAllFAQPages({ previewData, variables }: APIOptions = {}): Promise<
		Maybe<Faq_pageConnectionConnection>
	> {
		const data = await this.fetchAPI(
			`query getAllMortgageSubPages($lang: String!) {
        allFaq_pages(lang: $lang) {
          edges {
            node {
              title
              image
              _meta {
                uid
                lang
              }
            }
          }
        }
      }`,
			{
				previewData,
				variables,
			}
		)
		return data.allFaq_pages
	}
}

export default AudurPrismicClient
